import React from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { Body5, Body7 } from '@ui';
import { FlatButton } from '@ui/buttons';
import { showSuccessBanner } from '@utils/application';
import { PROPOSAL_DECISIONS, PROPOSAL_STATUSES, decideProposals } from '@services/fmdc';
import { Disclosure } from '@features/feedView/Disclosure';
import { SelectionCheckbox } from './SelectionCheckbox';
import { SelectionBar } from './SelectionBar';
import { StatusPill } from './StatusPill';
import { useSelection } from './useSelection';
import { formatCount, formatDateTime, formatProposalStatus, EM_DASH } from './format';
import { PROPOSAL_STATUS_TOKENS, tokenFor } from './statusTokens';
import { DangerTransparentButton, HeaderCell, HeaderRow, LinkButton, Mono, Row, Table, TableScroll } from './styles';

// The catalog proposals, with an admin's bulk Approve / Reject over the pending ones. Only a
// pending proposal gets a checkbox: the API decides pending ones and counts the rest as skipped,
// so a selection that could include a decided row would only ever report a skip.

// ☐ | Proposed code | Label | Section | Source | Run | Rationale | Status | Decided
const GRID_COLUMNS =
  '24px minmax(160px, 1.5fr) minmax(150px, 1.6fr) minmax(110px, 1fr) minmax(150px, 1.5fr) minmax(56px, 0.5fr) minmax(170px, 1.8fr) minmax(100px, 0.9fr) minmax(120px, 1.1fr)';

// The full rationale keeps the author's line breaks.
const RationaleText = styled(Body7)`
  display: block;
  white-space: pre-wrap;
`;

const RATIONALE_PREVIEW = 60;

const RationaleCell = ({ rationale }) => {
  const [open, setOpen] = React.useState(false);
  if (!rationale) return <Body7 isLowContrast>{EM_DASH}</Body7>;
  if (rationale.length <= RATIONALE_PREVIEW) return <Body7>{rationale}</Body7>;
  return (
    <div>
      <Disclosure
        isOpen={open}
        onToggle={() => setOpen((v) => !v)}
        summary={
          <Body7 isLowContrast>{open ? 'Hide rationale' : `${rationale.slice(0, RATIONALE_PREVIEW).trim()}…`}</Body7>
        }
      >
        <RationaleText>{rationale}</RationaleText>
      </Disclosure>
    </div>
  );
};

const decisionLabel = (decision) => (decision === PROPOSAL_DECISIONS.APPROVED ? 'approved' : 'rejected');

export const ProposalsTable = ({ proposals, canAdmin = false, onDecided, onOpenRun }) => {
  const dispatch = useDispatch();
  const pendingIds = React.useMemo(
    () => (canAdmin ? proposals.filter((p) => p.status === PROPOSAL_STATUSES.PENDING).map((p) => p.id) : []),
    [proposals, canAdmin]
  );
  const selection = useSelection(pendingIds);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  const decide = async (decision) => {
    if (busy || selection.count === 0) return;
    setBusy(true);
    setError(null);
    const result = await decideProposals({ ids: selection.selectedIds, decision });
    setBusy(false);
    if (result.error) {
      // Selection kept so the admin can retry.
      setError(result.error);
      return;
    }
    const decided = result.result?.decided ?? 0;
    const skipped = result.result?.skipped ?? 0;
    showSuccessBanner(
      `${formatCount(decided)} ${decisionLabel(decision)}${
        skipped ? `, ${formatCount(skipped)} skipped (already decided)` : ''
      }`,
      null,
      dispatch
    );
    selection.clear();
    if (onDecided) onDecided();
  };

  return (
    <div>
      {canAdmin && (
        <SelectionBar count={selection.count} noun="proposal" onClear={selection.clear} error={error}>
          <FlatButton onClick={() => decide(PROPOSAL_DECISIONS.APPROVED)} disabled={busy}>
            {busy ? 'Deciding…' : 'Approve'}
          </FlatButton>
          <DangerTransparentButton onClick={() => decide(PROPOSAL_DECISIONS.REJECTED)} disabled={busy}>
            Reject
          </DangerTransparentButton>
        </SelectionBar>
      )}
      <TableScroll>
        <Table $minWidth={1080}>
          <HeaderRow $columns={GRID_COLUMNS}>
            <div>
              {canAdmin && pendingIds.length > 0 && (
                <SelectionCheckbox
                  isChecked={selection.isPageFullySelected}
                  isIndeterminate={selection.isPagePartiallySelected}
                  onChange={(shouldSelect) => selection.setPage(shouldSelect)}
                  ariaLabel="Select all pending proposals on this page"
                />
              )}
            </div>
            <HeaderCell>Proposed code</HeaderCell>
            <HeaderCell>Label</HeaderCell>
            <HeaderCell>Section</HeaderCell>
            <HeaderCell>Source</HeaderCell>
            <HeaderCell>Run</HeaderCell>
            <HeaderCell>Rationale</HeaderCell>
            <HeaderCell>Status</HeaderCell>
            <HeaderCell>Decided</HeaderCell>
          </HeaderRow>
          {proposals.map((proposal) => {
            const selectable = canAdmin && proposal.status === PROPOSAL_STATUSES.PENDING;
            const selected = selectable && selection.isSelected(proposal.id);
            return (
              <Row key={proposal.id} $columns={GRID_COLUMNS} $interactive={false} $selected={selected}>
                <div>
                  {selectable && (
                    <SelectionCheckbox
                      isChecked={selected}
                      onChange={() => selection.toggle(proposal.id)}
                      ariaLabel={`Select proposal ${proposal.proposed_code}`}
                    />
                  )}
                </div>
                <Mono>{proposal.proposed_code}</Mono>
                <Body5 bold>{proposal.proposed_label || EM_DASH}</Body5>
                <div>
                  <Body7>{proposal.section || EM_DASH}</Body7>
                  {proposal.sub_section && (
                    <div>
                      <Body7 isLowContrast>{proposal.sub_section}</Body7>
                    </div>
                  )}
                </div>
                <div>
                  <Body7>{proposal.source_label || EM_DASH}</Body7>
                  {proposal.source_location && (
                    <div>
                      <Mono>{proposal.source_location}</Mono>
                    </div>
                  )}
                </div>
                <div>
                  {proposal.run_id ? (
                    onOpenRun ? (
                      <LinkButton onClick={() => onOpenRun(proposal.run_id)}>#{proposal.run_id}</LinkButton>
                    ) : (
                      <Body7>#{proposal.run_id}</Body7>
                    )
                  ) : (
                    <Body7 isLowContrast>{EM_DASH}</Body7>
                  )}
                </div>
                <RationaleCell rationale={proposal.rationale} />
                <div>
                  <StatusPill
                    token={tokenFor(PROPOSAL_STATUS_TOKENS, proposal.status)}
                    label={formatProposalStatus(proposal.status)}
                  />
                </div>
                <div>
                  {proposal.decided_at ? (
                    <>
                      <Body7>{proposal.decided_by || EM_DASH}</Body7>
                      <div>
                        <Body7 isLowContrast title={proposal.decided_at}>
                          {formatDateTime(proposal.decided_at)}
                        </Body7>
                      </div>
                    </>
                  ) : (
                    <Body7 isLowContrast>{EM_DASH}</Body7>
                  )}
                </div>
              </Row>
            );
          })}
        </Table>
      </TableScroll>
    </div>
  );
};
